import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Switch,


} from '@pagespeedify/ui';
import {

    GitBranch,
    Brain,
    List,
    Plus,
    Trash2,
    MoreHorizontal,
    ArrowLeft,
    CheckCircle2,
} from 'lucide-react';
import { useApi } from '../lib/apiClient';
import { Task } from '../types';
import type {
    AgentPreferences,
    AgentGitExportBranding,
    AgentPrMode,
    GitHubStatusResponse,
} from '../types/agent';

// ============================================================================
// Types
// ============================================================================

interface SuperMemoryEntry {
    id: string;
    content: string;
    repo?: string | null;
    source?: string; // e.g., "task:abc123" or "manual"
    goal?: string | null;
    phase?: "planning" | "execution" | "discussion" | null;
    stackHints?: string[];
    frameworks?: string[];
    fileAnchors?: string[];
    issueCategories?: string[];
    stepActions?: string[];
    confidence?: number;
    successCount?: number;
    failureCount?: number;
    createdFromTaskId?: string | null;
    createdAt: string;
    updatedAt: string;
    lastUsedAt?: string;
}

// ============================================================================
// Overview Tab - Agent Task List
// ============================================================================

function AgentOverview() {
    const apiClient = useApi();
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<'in_progress' | 'complete'>('in_progress');

    const { data: tasksResponse, isLoading } = useQuery<{ data: { tasks: { tasks: Task[] } } }>({
        queryKey: ['agent-tasks'],
        queryFn: () => apiClient.get('/tasks?type=agent'),
        staleTime: 2 * 60 * 1000,
    });

    // Filter only agent-type tasks
    const allAgentTasks = (tasksResponse?.data?.tasks?.tasks || []).filter(t => t.type === 'agent');

    // Filter by status
    const inProgressTasks = allAgentTasks.filter(t => t.status === 'in_progress' || t.status === 'open');
    const completeTasks = allAgentTasks.filter(t => t.status === 'resolved' || t.status === 'closed');

    const filteredTasks = statusFilter === 'in_progress' ? inProgressTasks : completeTasks;

    const getStatusBadge = (status: string) => {
        const config: Record<string, { label: string; className: string }> = {
            open: { label: 'Ready for review', className: 'bg-purple-500/10 text-purple-600' },
            pending_approval: { label: 'Ready for review', className: 'bg-purple-500/10 text-purple-600' },
            in_progress: { label: 'In progress', className: 'bg-blue-500/10 text-blue-600' },
            resolved: { label: 'Completed', className: 'bg-green-500/10 text-green-600' },
            closed: { label: 'Archived', className: 'bg-muted text-muted-foreground' },
        };
        const c = config[status] || { label: status, className: 'bg-muted text-muted-foreground' };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.className}`}>{c.label}</span>;
    };

    const formatTimeAgo = (date: string) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 30) return `Completed ${Math.floor(days / 30)} months ago`;
        if (days > 0) return `Completed ${days} days ago`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} hours ago`;
        return 'Just now';
    };

    return (

        <div className="h-full flex flex-col">
            <div className="p-6 pb-0 flex-shrink-0">
                {/* Tabs */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                        {['in_progress', 'complete'].map((filter) => (
                            <button
                                key={filter}
                                onClick={() => setStatusFilter(filter as any)}
                                className={`px-3 py-1.5 text-sm rounded ${statusFilter === filter
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {filter === 'in_progress' ? 'In Progress' : 'Completed'}
                            </button>
                        ))}
                    </div>
                    <Button size="sm" onClick={() => navigate('/agent')} className="gap-1">
                        <Plus className="h-4 w-4" />
                        New
                    </Button>
                </div>
            </div>

            {/* Task List */}
            <div className="flex-1 overflow-y-auto p-6 pt-0">
                {isLoading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <List className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No agent tasks found</p>
                        <Button variant="link" onClick={() => navigate('/agent')}>Start a new agent task →</Button>
                    </div>
                ) : (
                    <div className="space-y-1 pb-6">
                        {filteredTasks.map(task => (
                            <TaskRow key={task.id} task={task} getStatusBadge={getStatusBadge} formatTimeAgo={formatTimeAgo} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

}

function TaskRow({ task, getStatusBadge, formatTimeAgo }: {
    task: Task;
    getStatusBadge: (status: string) => JSX.Element;
    formatTimeAgo: (date: string) => string;
}) {
    const navigate = useNavigate();
    const isPaused = task.status === 'in_progress' && task.tag === 'paused';

    return (
        <div
            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer group"
            onClick={() => navigate(`/agent?taskId=${task.id}`)}
        >
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{task.title || task.description || 'Untitled task'}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="inline-flex items-center gap-1">
                        {task.status === 'in_progress' && !isPaused && (
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        )}
                        {isPaused ? 'This session is paused' : formatTimeAgo(task.updated_at)}
                    </span>
                </p>
            </div>
            <div className="flex items-center gap-2">
                {isPaused ? (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/10 text-yellow-600">Paused</span>
                ) : (
                    getStatusBadge(task.status)
                )}
                <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-muted rounded">
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                </button>
            </div>
        </div>
    );
}

// ============================================================================
// Environment Tab - Repo/Branch Config
// ============================================================================

function AgentEnvironment() {
    const apiClient = useApi();
    const [selectedRepo, setSelectedRepo] = useState<string>('');
    const [selectedBranch, setSelectedBranch] = useState<string>('');
    const [repoSettings, setRepoSettings] = useState<Record<string, any>>({});
    const [installCommand, setInstallCommand] = useState<string>('');
    const [buildCommand, setBuildCommand] = useState<string>('');
    const [testCommand, setTestCommand] = useState<string>('');
    const [saved, setSaved] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [prMode, setPrMode] = useState<AgentPrMode>('per_task');
    const [gitExportBranding, setGitExportBranding] = useState<AgentGitExportBranding>('swayashin');
    const [outputSaved, setOutputSaved] = useState(false);
    const [isSavingOutput, setIsSavingOutput] = useState(false);
    const [outputError, setOutputError] = useState<string | null>(null);

    // Load preferences from API
    const { data: prefsData } = useQuery<{ data: { agent: AgentPreferences } }>({
        queryKey: ['agent-preferences'],
        queryFn: () => apiClient.get('/agent/preferences'),
        staleTime: 5 * 60 * 1000,
    });

    const { data: githubStatus, isLoading: isLoadingGitHubStatus } = useQuery<GitHubStatusResponse>({
        queryKey: ['github-status'],
        queryFn: () => apiClient.get('/github/status'),
        staleTime: 60 * 1000,
    });

    // Populate state when preferences load
    useEffect(() => {
        if (prefsData?.data?.agent) {
            const prefs = prefsData.data.agent;
            const currentRepo = prefs.defaultRepo || '';
            const allRepos = prefs.repositories || {};

            setSelectedRepo(currentRepo);
            setSelectedBranch(prefs.defaultBranch || '');
            setRepoSettings(allRepos);
            setPrMode(prefs.prMode === 'shared_repo_branch' ? 'shared_repo_branch' : 'per_task');
            setGitExportBranding(prefs.gitExportBranding === 'user_only' ? 'user_only' : 'swayashin');

            // Initialize inputs for current repo
            // Fallback to top-level legacy prefs if available and not in map
            const currentSettings = allRepos[currentRepo] || {
                installCommand: prefs.installCommand,
                buildCommand: prefs.buildCommand,
                testCommand: prefs.testCommand
            };

            setInstallCommand(currentSettings.installCommand || '');
            setBuildCommand(currentSettings.buildCommand || '');
            setTestCommand(currentSettings.testCommand || '');
        }
    }, [prefsData]);

    // Fetch repos
    const { data: repos, isLoading: isLoadingRepos } = useQuery<any[]>({
        queryKey: ['github-repos'],
        queryFn: async () => {
            const response = await apiClient.get<any>('/github/repos');
            return response.data || [];
        },
        staleTime: 5 * 60 * 1000,
    });

    // Fetch branches
    const { data: repoTreeData, isLoading: isLoadingBranches } = useQuery<{ branches: string[]; defaultBranch: string }>({
        queryKey: ['repo-tree-env', selectedRepo],
        queryFn: async () => {
            const [owner, repo] = selectedRepo.split('/');
            const response = await apiClient.get<{ data: { branches: string[]; defaultBranch: string } }>(
                `/github/repos/${owner}/${repo}/tree`
            );
            return response.data;
        },
        enabled: !!selectedRepo && selectedRepo.includes('/'),
        staleTime: 2 * 60 * 1000,
    });

    const branches = repoTreeData?.branches || [];

    const handleRepoChange = (newRepo: string) => {
        // Snapshot current settings to local map before switching
        if (selectedRepo) {
            const currentParams = { installCommand, buildCommand, testCommand };
            setRepoSettings(prev => ({
                ...prev,
                [selectedRepo]: currentParams
            }));
            // Note: setRepoSettings is async, so we use a local updated map for the lookup below if needed
            // But actually we need to verify if the NEW repo exists in the OLD map + current update.
            // Simplified: We just update the map state.
            // For the lookup, we look at `repoSettings` (current state) + fallback.
            // Wait, if I switch A -> B, `repoSettings` still has old A. 
            // Better to construct updated map locally.
            const updatedMap = { ...repoSettings, [selectedRepo]: currentParams };
            setRepoSettings(updatedMap);

            // Load new repo settings
            const nextSettings = updatedMap[newRepo] || {};
            setInstallCommand(nextSettings.installCommand || '');
            setBuildCommand(nextSettings.buildCommand || '');
            setTestCommand(nextSettings.testCommand || '');
        } else {
            // Initial switch from empty
            const nextSettings = repoSettings[newRepo] || {};
            setInstallCommand(nextSettings.installCommand || '');
            setBuildCommand(nextSettings.buildCommand || '');
            setTestCommand(nextSettings.testCommand || '');
        }

        setSelectedRepo(newRepo); // This triggers branch fetch
        setValidationError(null);
    };

    const handleSaveEnvironment = async () => {
        if (!selectedRepo) return;
        setIsSaving(true);
        setValidationError(null);

        // Update map with current inputs
        const updatedRepositories = {
            ...repoSettings,
            [selectedRepo]: { installCommand, buildCommand, testCommand }
        };

        try {
            await apiClient.put('/agent/preferences', {
                defaultRepo: selectedRepo,
                defaultBranch: selectedBranch,
                // Send current as defaults/active too for compatibility
                installCommand,
                buildCommand,
                testCommand,
                repositories: updatedRepositories
            });

            setRepoSettings(updatedRepositories); // Commit to state
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (error: any) {
            console.error('Failed to save preferences:', error);
            const errorMessage = error?.response?.data?.message || 'Failed to save settings';
            setValidationError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <div className="h-full overflow-y-auto">
            <div className="flex-1 p-6 max-w-2xl">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Default Repository</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Repository</label>
                            {isLoadingRepos ? (
                                <Skeleton className="h-10 w-full" />
                            ) : (
                                <Select value={selectedRepo} onValueChange={handleRepoChange}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select default repository..." />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px] overflow-y-auto">
                                        {(repos || []).map((repo: any) => {
                                            const repoName = repo.full_name || repo.name || String(repo);
                                            return (
                                                <SelectItem key={repoName} value={repoName}>
                                                    <span className="font-mono text-sm">{repoName}</span>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Branch</label>
                            {isLoadingBranches && selectedRepo ? (
                                <Skeleton className="h-10 w-full" />
                            ) : (
                                <Select value={selectedBranch} onValueChange={setSelectedBranch} disabled={!selectedRepo}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select default branch..." />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60 overflow-auto">
                                        {branches.map((branch: string) => (
                                            <SelectItem key={branch} value={branch}>
                                                <span className="text-sm">{branch}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Install Command</label>
                            <p className="text-xs text-muted-foreground">
                                Command to install dependencies (e.g., <code className="bg-muted px-1 rounded">npm install</code>)
                            </p>
                            <input
                                type="text"
                                value={installCommand}
                                onChange={(e) => setInstallCommand(e.target.value)}
                                placeholder="npm install"
                                className="w-full p-3 rounded-lg bg-muted/50 border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Build Command</label>
                            <p className="text-xs text-muted-foreground">
                                Command to build your project (e.g., <code className="bg-muted px-1 rounded">npm run build</code>)
                            </p>
                            <input
                                type="text"
                                value={buildCommand}
                                onChange={(e) => setBuildCommand(e.target.value)}
                                placeholder="npm run build"
                                className="w-full p-3 rounded-lg bg-muted/50 border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Test Command <span className="text-muted-foreground font-normal">(optional)</span></label>
                            <p className="text-xs text-muted-foreground">
                                Command to run tests. Leave empty to skip test verification.
                            </p>
                            <input
                                type="text"
                                value={testCommand}
                                onChange={(e) => setTestCommand(e.target.value)}
                                placeholder="npm test"
                                className="w-full p-3 rounded-lg bg-muted/50 border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>

                        {validationError && (
                            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                {validationError}
                            </div>
                        )}

                        <Button onClick={handleSaveEnvironment} disabled={!selectedRepo || isSaving} className="w-full">
                            {saved ? (
                                <>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Saved!
                                </>
                            ) : isSaving ? (
                                'Saving...'
                            ) : (
                                'Save Settings'
                            )}
                        </Button>
                    </CardContent>
                </Card>

                <Card className="mt-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Git &amp; PR Output</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">PR Mode</label>
                            <p className="text-xs text-muted-foreground">
                                Choose whether exported changes should stay isolated to this task or roll into one shared PR for this repo and base branch.
                            </p>
                            <Select value={prMode} onValueChange={(value) => setPrMode(value as AgentPrMode)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select PR mode" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="per_task">One PR per task</SelectItem>
                                    <SelectItem value="shared_repo_branch">Add to shared PR for this repo/branch</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                One PR per task keeps reviews isolated. Shared PR mode batches unmerged work for the same repo and base branch into one rolling PR line.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Git/PR Branding</label>
                            <p className="text-xs text-muted-foreground">
                                Control how exported PR titles, PR bodies, and commit authorship are presented.
                            </p>
                            <Select value={gitExportBranding} onValueChange={(value) => setGitExportBranding(value as AgentGitExportBranding)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select branding mode" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="swayashin">Swayashin branding</SelectItem>
                                    <SelectItem value="user_only">User-only branding</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                User-only branding removes Swayashin from PR title/body and uses your GitHub identity for commit authorship.
                            </p>
                        </div>

                        {gitExportBranding === 'user_only' && (
                            <div className={`p-3 rounded-lg border text-sm ${githubStatus?.isLinked
                                ? (githubStatus?.hasAuthoringIdentityScope
                                    ? 'bg-green-500/10 border-green-500/20 text-green-700'
                                    : 'bg-amber-500/10 border-amber-500/20 text-amber-700')
                                : 'bg-amber-500/10 border-amber-500/20 text-amber-700'
                                }`}>
                                {isLoadingGitHubStatus ? (
                                    'Checking GitHub authoring access...'
                                ) : githubStatus?.isLinked && githubStatus?.hasAuthoringIdentityScope ? (
                                    'GitHub is linked with the scopes needed for user-only commit authorship.'
                                ) : githubStatus?.isLinked ? (
                                    'Reconnect GitHub with read:user and user:email so commits can be authored as you.'
                                ) : (
                                    'Link GitHub with read:user and user:email before using user-only commit authorship.'
                                )}
                                {(!isLoadingGitHubStatus && (!githubStatus?.isLinked || !githubStatus?.hasAuthoringIdentityScope)) && (
                                    <div className="mt-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                                setOutputError(null);
                                                try {
                                                    const response = await apiClient.post<{ redirectUrl?: string; isAlreadyLinked?: boolean }>(
                                                        '/github/auth/start',
                                                        { source: 'agent-settings' }
                                                    );
                                                    if (response?.redirectUrl) {
                                                        window.location.href = response.redirectUrl;
                                                        return;
                                                    }
                                                    if (response?.isAlreadyLinked) {
                                                        setOutputError('GitHub is already linked, but the current token still needs the extra authoring scopes. Please disconnect and reconnect GitHub.');
                                                        return;
                                                    }
                                                    setOutputError('Could not start GitHub reconnect.');
                                                } catch (error: any) {
                                                    setOutputError(error?.response?.data?.message || 'Failed to start GitHub reconnect.');
                                                }
                                            }}
                                        >
                                            {githubStatus?.isLinked ? 'Reconnect GitHub' : 'Link GitHub'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {outputError && (
                            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                {outputError}
                            </div>
                        )}

                        <Button
                            onClick={async () => {
                                setIsSavingOutput(true);
                                setOutputError(null);
                                try {
                                    if (gitExportBranding === 'user_only' && !githubStatus?.hasAuthoringIdentityScope) {
                                        setOutputError('Reconnect GitHub with read:user and user:email before enabling user-only commit authorship.');
                                        return;
                                    }
                                    await apiClient.put('/agent/preferences', {
                                        prMode,
                                        gitExportBranding
                                    });
                                    setOutputSaved(true);
                                    setTimeout(() => setOutputSaved(false), 2000);
                                } catch (error: any) {
                                    console.error('Failed to save git export settings:', error);
                                    setOutputError(error?.response?.data?.message || 'Failed to save Git/PR output settings');
                                } finally {
                                    setIsSavingOutput(false);
                                }
                            }}
                            disabled={isSavingOutput}
                            className="w-full"
                        >
                            {outputSaved ? (
                                <>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Saved!
                                </>
                            ) : isSavingOutput ? (
                                'Saving...'
                            ) : (
                                'Save Git/PR Output'
                            )}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

// ============================================================================
// Super Memory Tab
// ============================================================================

function SuperMemory() {
    const apiClient = useApi();
    const [isEnabled, setIsEnabled] = useState(true);
    const [memories, setMemories] = useState<SuperMemoryEntry[]>([]);
    const [newMemory, setNewMemory] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load from API
    const { data: memoryData, refetch } = useQuery<{ data: { enabled: boolean; entries: SuperMemoryEntry[] } }>({
        queryKey: ['agent-memory'],
        queryFn: () => apiClient.get('/agent/memory'),
        staleTime: 5 * 60 * 1000,
    });

    // Populate state when memory loads
    useEffect(() => {
        if (memoryData?.data) {
            setMemories(memoryData.data.entries || []);
            setIsEnabled(memoryData.data.enabled !== false);
        }
    }, [memoryData]);

    // Save to API
    const saveMemories = async (newMemories: SuperMemoryEntry[], enabled: boolean) => {
        setIsSaving(true);
        try {
            await apiClient.put('/agent/memory', {
                enabled,
                entries: newMemories
            });
            setMemories(newMemories);
            setIsEnabled(enabled);
            refetch();
        } catch (error) {
            console.error('Failed to save super memory:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = (enabled: boolean) => {
        saveMemories(memories, enabled);
    };

    const handleAdd = () => {
        if (!newMemory.trim()) return;
        const entry: SuperMemoryEntry = {
            id: `mem_${Date.now()}`,
            content: newMemory.trim(),
            source: 'manual',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        saveMemories([entry, ...memories], isEnabled);
        setNewMemory('');
        setShowAddForm(false);
    };

    const handleDelete = (id: string) => {
        saveMemories(memories.filter(m => m.id !== id), isEnabled);
    };

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Calculate pagination
    const totalPages = Math.ceil(memories.length / ITEMS_PER_PAGE);
    const paginatedMemories = memories.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(p => p + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(p => p - 1);
    };

    // Auto-save handler
    const handleAutoSave = (id: string, newContent: string) => {
        const mem = memories.find(m => m.id === id);
        if (mem && mem.content !== newContent && newContent.trim()) {
            saveMemories(
                memories.map(m => m.id === id ? { ...m, content: newContent.trim(), updatedAt: new Date().toISOString() } : m),
                isEnabled
            );
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div className="p-6 pb-0 flex-shrink-0">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold">Super Memory</h2>
                        <p className="text-sm text-muted-foreground">
                            Allow the agent to learn from past interactions.
                        </p>
                    </div>
                    <Button size="sm" onClick={() => setShowAddForm(true)} className="gap-1">
                        <Plus className="h-4 w-4" />
                        Add Memory
                    </Button>
                </div>

                {/* Status Toggle (Simple Box) */}
                <div className="mb-6 border border-border rounded-lg bg-background p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm font-medium">
                            {isEnabled ? 'Memory Active' : 'Memory Disabled'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground mr-2">
                            {isEnabled ? 'On' : 'Off'}
                        </span>
                        <Switch checked={isEnabled} onCheckedChange={handleToggle} disabled={isSaving} />
                    </div>
                </div>

                {/* Add Form */}
                {showAddForm && (
                    <div className="mb-4 border border-border rounded-lg bg-muted/20 p-4">
                        <textarea
                            value={newMemory}
                            onChange={(e) => setNewMemory(e.target.value)}
                            placeholder="Add a memory entry..."
                            className="w-full h-24 p-3 rounded-lg bg-background border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <div className="flex justify-end gap-2 mt-2">
                            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
                            <Button size="sm" onClick={handleAdd} disabled={isSaving}>
                                {isSaving ? 'Adding...' : 'Add'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Memory List */}
            <div className="flex-1 overflow-y-auto p-6 pt-0">
                {memories.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No memories yet</p>
                        <p className="text-xs mt-1">Memories are automatically created from completed tasks</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-1 pb-6">
                            {paginatedMemories.map((mem) => (
                                <div key={mem.id} className="group relative flex items-start gap-3 px-3 py-2 rounded-lg border border-transparent hover:border-border/50 hover:bg-muted/10 transition-colors">
                                    <textarea
                                        defaultValue={mem.content}
                                        onBlur={(e) => handleAutoSave(mem.id, e.target.value)}
                                        className="flex-1 min-h-[22px] bg-transparent border-none text-sm resize-y focus:outline-none focus:ring-0 p-0 leading-normal"
                                        style={{ fieldSizing: 'content' } as any} // experimental or use standard approach
                                    />
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleDelete(mem.id)}
                                            className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded text-muted-foreground transition-colors"
                                            title="Delete memory"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-2 pb-6 border-t border-border mt-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <span className="text-xs text-muted-foreground">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

}

// ============================================================================
// Main Page Component
// ============================================================================

export default function AgentSettingsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<'overview' | 'environment' | 'memory'>(
        (searchParams.get('tab') as any) || 'overview'
    );

    return (
        <div className="flex h-full">
            {/* Left Sidebar */}
            <div className="w-48 border-r border-border bg-muted/20 p-4">
                <button
                    onClick={() => navigate('/agent')}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Agent
                </button>

                <nav className="space-y-1">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${activeTab === 'overview'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <List className="h-4 w-4" />
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('environment')}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${activeTab === 'environment'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <GitBranch className="h-4 w-4" />
                        Environment
                    </button>
                    <button
                        onClick={() => setActiveTab('memory')}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${activeTab === 'memory'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                    >
                        <Brain className="h-4 w-4" />
                        Super memory
                    </button>
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'overview' && <AgentOverview />}
                {activeTab === 'environment' && <AgentEnvironment />}
                {activeTab === 'memory' && <SuperMemory />}
            </div>
        </div>
    );
}
